import React from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Users, Settings, Building2, DollarSign, UserCog, FileText, Briefcase, Network, UserCheck, Clock, CalendarDays, TrendingUp, CreditCard, Play, Layers } from 'lucide-react';

const HCMDashboard = () => {
  const navigate = useNavigate();

  const modules = [
    {
      title: 'Personnel Administration',
      description: 'Manage employee master data, infotypes, and HR records',
      icon: Users,
      color: 'bg-orange-500',
      items: [
        { label: 'Employee Directory', path: '/hcm/employees', icon: Users },
        { label: 'Create Employee', path: '/hcm/employees/create', icon: UserCog },
        { label: 'Personnel Actions', path: '/hcm/personnel-actions', icon: FileText },
      ]
    },
    {
      title: 'Organizational Management',
      description: 'Manage org structure, jobs, positions, and reporting lines',
      icon: Network,
      color: 'bg-orange-500',
      items: [
        { label: 'Org Units', path: '/hcm/om/org-units', icon: Building2 },
        { label: 'Jobs', path: '/hcm/om/jobs', icon: Briefcase },
        { label: 'Positions', path: '/hcm/om/positions', icon: UserCheck },
        { label: 'Org Chart', path: '/hcm/om/org-chart', icon: Network },
      ]
    },
    {
      title: 'Time Management',
      description: 'Manage absences, attendances, work schedules, and quotas',
      icon: Clock,
      color: 'bg-orange-500',
      items: [
        { label: 'Time Dashboard', path: '/hcm/time', icon: Clock },
        { label: 'Absences', path: '/hcm/time/absences', icon: CalendarDays },
        { label: 'Attendances', path: '/hcm/time/attendances', icon: Clock },
        { label: 'Employee Quotas', path: '/hcm/time/employee-quotas', icon: TrendingUp },
      ]
    },
    {
      title: 'Payroll Accounting',
      description: 'Process payroll, manage taxes, and bank transfers',
      icon: DollarSign,
      color: 'bg-green-500',
      items: [
        { label: 'Payroll Dashboard', path: '/hcm/payroll', icon: DollarSign },
        { label: 'Payroll Runs', path: '/hcm/payroll/runs', icon: Play },
        { label: 'Payroll Results', path: '/hcm/payroll/results', icon: FileText },
        { label: 'Bank Transfers', path: '/hcm/payroll/bank-transfers', icon: CreditCard },
      ]
    },
    {
      title: 'Configuration',
      description: 'Configure HCM master data and organizational settings',
      icon: Settings,
      color: 'bg-orange-500',
      items: [
        { label: 'Enterprise Structure', path: '/hcm/config/enterprise-structure', icon: Building2 },
        { label: 'Pay Scale Configuration', path: '/hcm/config/pay-scale', icon: DollarSign },
        { label: 'Wage Types', path: '/hcm/config/wage-types', icon: Briefcase },
      ]
    }
  ];

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="hcm-dashboard">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Human Capital Management</h1>
          <p className="text-gray-600 mt-1">Manage your workforce, organizational structure, and HR configurations</p>
        </div>

        {/* Module Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {modules.map((module, idx) => (
            <Card key={idx} className="p-6 hover:shadow-lg transition-shadow" data-testid={`hcm-module-${idx}`}>
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${module.color}`}>
                  <module.icon className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-gray-900">{module.title}</h2>
                  <p className="text-sm text-gray-600 mt-1">{module.description}</p>
                  
                  <div className="mt-4 space-y-2">
                    {module.items.map((item, itemIdx) => (
                      <button
                        key={itemIdx}
                        onClick={() => navigate(item.path)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 text-left transition-colors border border-gray-100"
                        data-testid={`hcm-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <item.icon className="h-4 w-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-700">{item.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8">
          <Card className="p-4" data-testid="stat-employees">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Users className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Employees</p>
                <p className="text-xl font-semibold text-gray-900">-</p>
              </div>
            </div>
          </Card>
          <Card className="p-4" data-testid="stat-areas">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Building2 className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Personnel Areas</p>
                <p className="text-xl font-semibold text-gray-900">-</p>
              </div>
            </div>
          </Card>
          <Card className="p-4" data-testid="stat-actions">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FileText className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Pending Actions</p>
                <p className="text-xl font-semibold text-gray-900">-</p>
              </div>
            </div>
          </Card>
          <Card className="p-4" data-testid="stat-groups">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Layers className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Employee Groups</p>
                <p className="text-xl font-semibold text-gray-900">-</p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default HCMDashboard;
